import React from "react";
import FormattingUtils from "helpers/FormattingUtils";
import { UNKNOWN_USER_NAME } from "rtr-constants";
import PropTypes from "prop-types";
import { differenceInYears } from "date-fns";
import { parseISOWithoutTime } from "helpers/date-helpers";

class ReviewProfile extends React.Component {
  static propTypes = {
    moment: PropTypes.shape({
      uploadedAt: PropTypes.any,
      reviewId: PropTypes.any,
      aggregateComments: PropTypes.string,
      rating: PropTypes.number,
      caption: PropTypes.string,
      content: PropTypes.any,
      fitFeedbackMap: PropTypes.object,
      numReviewsByUser: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      happinessSurveyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      isStaff: PropTypes.bool,
      isAnonymous: PropTypes.bool,
    }).isRequired,
    reviewer: PropTypes.object.isRequired,
    isUserSpecificReview: PropTypes.bool,
    product: PropTypes.object,
  };

  topContributorThreshold = 2;

  userNameDisplay = () => {
    if (!this.props.reviewer || this.props.reviewer?.firstName === UNKNOWN_USER_NAME) {
      return UNKNOWN_USER_NAME;
    }

    const name = this.props.reviewer.nickName || this.props.reviewer.firstName;

    if (name) {
      return FormattingUtils.capitalize(name);
    }

    return UNKNOWN_USER_NAME;
  };

  displayUserSpecificUrl = () => {
    const { moment, product, isUserSpecificReview } = this.props;
    if (isUserSpecificReview || moment?.isAnonymous) {
      return;
    }

    const firstName = this.userNameDisplay();

    const url = "/user_reviews?reviewId=" + moment?.reviewId + "&displayName=" + product?.displayName;
    return <a className="reviewer-view-user-link" href={url}>{`Browse ${firstName ? firstName : ""}'s reviews`}</a>;
  };

  reviewerStatMarkup(label, text, key) {
    return (
      <div className="label" key={key}>
        <span className="review-detail-label">{label}</span>
        <strong className="review-detail-value">{text}</strong>
      </div>
    );
  }

  reviewDetails = () => {
    const reviewDetails = [
      { key: "sizeWorn", label: "Size worn: " },
      { key: "fit", label: "Overall fit: " },
      { key: "eventType", label: "Rented for: " },
    ];

    return reviewDetails
      .filter(detail => this.props.moment?.[detail.key])
      .map((detail, i) => this.reviewDetailMarkup(detail.label, detail.key, i));
  };

  reviewDetailMarkup = (label, data, i) => {
    return (
      <div key={i}>
        <div className="review-detail-label label">{label}</div>
        <div className="review-detail-value label">{this.props.moment[data]}</div>
      </div>
    );
  };

  reviewerStats = () => {
    const { reviewer } = this.props;
    const stats = [
      { key: "usStandardSize", label: "Usually wears: " },
      {
        key: "heightInches",
        label: "Height: ",
        fn: function (data) {
          return FormattingUtils.formatHeight(data);
        },
      },
      {
        key: "birthday",
        label: "Age: ",
        fn: function (data) {
          return differenceInYears(new Date(), parseISOWithoutTime(data));
        },
      },
      { key: "bustSize", label: "Bust size: " },
      { key: "bodyType", label: "Body type: " },
      {
        key: "weightPounds",
        label: "Weight: ",
        fn: function (data) {
          return data + "lbs";
        },
      },
    ];

    if (!reviewer) {
      return;
    }

    return stats
      .filter(stat => reviewer?.[stat.key])
      .map(stat => {
        const clone = { ...stat };

        clone.text = stat.fn ? stat.fn(reviewer[stat.key]) : reviewer[stat.key];

        return clone;
      })
      .map(stat => {
        const key = this.props.moment.reviewId + stat.key;

        return this.reviewerStatMarkup(stat.label, stat.text, key);
      });
  };

  reviewerIsStaffOrTopContributor = () => {
    if (this.props.moment?.isAnonymous) return;
    if (this.props.moment?.isStaff) {
      return <div className="top-contributor label">Team RTR</div>;
    }
    if (this.props.moment?.numReviewsByUser > this.topContributorThreshold) {
      return <div className="top-contributor label">Top Contributor</div>;
    }
  };

  render() {
    return (
      <div className="reviewer-info">
        <div className="reviewer-nickname dek-one">
          <span itemProp="author">{this.userNameDisplay()}</span>
          {this.reviewerIsStaffOrTopContributor()}
        </div>
        {this.reviewDetails()}
        <div className="reviewer-stats">{this.reviewerStats()}</div>
        {this.displayUserSpecificUrl()}
      </div>
    );
  }
}

export default ReviewProfile;
